import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Divider,
  Typography,
  Button,
} from '@mui/material'
import { ClosingAwareModal } from '~/components/closing-aware-modal'
import { useOrgParams } from '~/common/use-org-params'
import { useOrgContextDispatch, setLastSeasonId, bumpSeasonListKey } from '~/common/context/organization/org-context'
import { useTasksInOrg } from '~/tasks/hooks/use-org-tasks'
import { useCropData } from '~/crop/crop-list/use-crop-data'
import { useClimate } from '~/crop/crop-schedule/hooks/use-climate'
import { useCurrentUser } from '~/common/use-current-user'
import { supabaseGetter } from '~/api/supabase-getter'
import { SeasonWarningsBanner } from './components/season-warnings-banner'
import { SeasonMetrics } from './components/season-metrics'
import { CropTransferList } from './components/crop-transfer-list'
import { EndSeasonConfirmationModal } from './end-season-confirmation-modal'
import { useEndSeason } from './hooks/use-end-season'
import { useCreateNextSeason } from './hooks/use-create-next-season'

const CURRENT_YEAR = new Date().getFullYear()

/**
 * End Season modal.
 * Shows a season snapshot, lets the user choose which crops carry over,
 * then confirms the archive action (stubbed).
 *
 * @param {boolean} open
 * @param {Function} onClose
 */
export const EndSeasonModal = ({ open, onClose }) => {
  const { farmId } = useOrgParams()
  const [, setSearchParams] = useSearchParams()
  const { tasks } = useTasksInOrg()
  const { crops, loading: cropsLoading } = useCropData()
  const { climateData } = useClimate({ farmId })
  const { currentUser } = useCurrentUser()
  const { endSeason } = useEndSeason()
  const { createNextSeason } = useCreateNextSeason()
  const dispatch = useOrgContextDispatch()

  const [archive, setArchive] = useState([])
  const [keep, setKeep] = useState([])
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [currentSeasonId, setCurrentSeasonId] = useState(null)

  // Frost dates for the NEXT year — used to anchor carry-over phase timings
  const nextYear = CURRENT_YEAR + 1
  const frostDates = useMemo(() => {
    const lfd = climateData?.avg_lfd
    const ffd = climateData?.avg_ffd
    if (!lfd || !ffd) return null
    return {
      lfd: new Date(nextYear, 0, lfd),  // DOY → Date in next year
      ffd: new Date(nextYear, 0, ffd),
    }
  }, [climateData, nextYear])

  // Fetch the active season ID when the modal opens
  useEffect(() => {
    if (!open || !farmId) return
    supabaseGetter.seasons.getActiveByFarm(farmId).then(({ data }) => {
      setCurrentSeasonId(data?.id ?? null)
    })
  }, [open, farmId])

  // Partition crops into archive / keep based on lifecycle when crops load
  useEffect(() => {
    if (cropsLoading || !crops.length) return
    const perennials = crops.filter((c) => c.crop_details?.lifecycle_code === 'perennial')
    const annuals = crops.filter((c) => c.crop_details?.lifecycle_code !== 'perennial')
    setArchive(annuals)
    setKeep(perennials)
  }, [crops, cropsLoading])

  const handleClose = useCallback(() => {
    setConfirmOpen(false)
    onClose?.()
  }, [onClose])

  const handleConfirm = useCallback(async () => {
    if (!currentSeasonId || !farmId) return
    setIsSubmitting(true)
    try {
      // 1. Create next season first (need its ID for carry-over schedules)
      const { data: nextSeason, error: nextSeasonError } = await createNextSeason({
        farmId,
        currentYear: CURRENT_YEAR,
      })
      if (nextSeasonError) throw nextSeasonError

      // Point the season selector at the new season and trigger a re-fetch
      dispatch(setLastSeasonId({ seasonId: nextSeason.id }))
      dispatch(bumpSeasonListKey())

      // 2. End current season: create carry-over schedules (with phases), mark ended
      const { error: endError } = await endSeason({
        currentSeasonId,
        cropsToKeep: keep,
        nextSeasonId: nextSeason.id,
        userId: currentUser?.id,
        frostDates,
      })
      if (endError) throw endError

      // 3. Drop seasonId from URL — returns to active season view
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        next.delete('seasonId')
        return next
      })

      // 4. Invalidate relevant caches
      supabaseGetter.seasons.invalidate()
      supabaseGetter.crops.invalidate()
      supabaseGetter.cropSchedules.invalidate()
      supabaseGetter.cropPhases.invalidate()
      supabaseGetter.tasks.invalidate()

      handleClose()
    } catch (err) {
      console.error('[EndSeason] error:', err)
    } finally {
      setIsSubmitting(false)
    }
  }, [currentSeasonId, farmId, createNextSeason, endSeason, keep, currentUser?.id, frostDates, setSearchParams, handleClose, dispatch])

  return (
    <>
      <ClosingAwareModal
        open={open}
        onClose={handleClose}
        loading={isSubmitting}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflowY: 'auto',
          py: 4,
        }}
      >
        <Box
          component="form"
          sx={{
            position: 'relative',
            width: { xs: '90vw', md: '80vw', lg: '70vw' },
            bgcolor: 'background.paper',
            borderRadius: '0.5rem',
            boxShadow: 24,
            p: '1rem 2rem',
            my: 'auto',
          }}
        >
          <Typography variant="h4">Complete {CURRENT_YEAR} Season</Typography>

          <SeasonWarningsBanner tasks={tasks} climateData={climateData} />

          <Divider flexItem sx={{ mt: '0.75rem', mb: '0.5rem', borderBottomWidth: '1px' }} />

          {/* ── Metrics snapshot ── */}
          <Typography variant="h6" sx={{ mb: '0.25rem' }}>
            Season Metrics
          </Typography>
          <SeasonMetrics
            crops={crops}
            gdd5Series={climateData?.gdd5_series ?? null}
            gdd10Series={climateData?.gdd10_series ?? null}
            lfd={climateData?.avg_lfd != null ? Math.round(climateData.avg_lfd) : null}
            ffd={climateData?.avg_ffd != null ? Math.round(climateData.avg_ffd) : null}
          />

          <Divider flexItem sx={{ my: '0.5rem', borderBottomWidth: '1px' }} />

          {/* ── Crop carry-over ── */}
          <Typography variant="h6" sx={{ mb: '0.25rem' }}>
            Crops to Carry Over
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: '0.75rem' }}>
            Crops on the left will be archived when the season ends. Move crops to the right
            to keep them active into next season. Succession crops are excluded — they are
            always archived with their parent.
          </Typography>

          <CropTransferList
            archive={archive}
            setArchive={setArchive}
            keep={keep}
            setKeep={setKeep}
          />

          {/* ── Action buttons ── */}
          <Box sx={{ mt: '1.5rem', display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <Button variant="outlined" size="large" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              size="large"
              onClick={() => setConfirmOpen(true)}
            >
              End {CURRENT_YEAR} Season
            </Button>
          </Box>
        </Box>
      </ClosingAwareModal>

      <EndSeasonConfirmationModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        year={CURRENT_YEAR}
        tasks={tasks}
        climateData={climateData}
        isSubmitting={isSubmitting}
      />
    </>
  )
}
